// The six access states a person on a household can be in, and every rule that
// hangs off them — household-app-access, R-ACC-5.
//
// WHY THIS LIVES IN @bbbk/schema RATHER THAN IN THE WORKER. Three surfaces read
// these states: the desk's App access column draws them, the API derives them
// from `portal_invites` + the KV allowlist, and the customer's own door reads
// whether its link is still good. A state machine copied into three places is
// three chances to disagree about what "Signing up" means — and the one that
// matters is the ORDER of the derivation, which a copy gets wrong silently.
//
// THE STATES ARE DERIVED, NEVER STORED. There is no status column on
// `portal_invites` and there is not meant to be: every state below falls out of
// four timestamps on the invite row plus one question the allowlist answers.
// Nothing to set, nothing to drift, and no migration when the rules change.

/**
 * The life cycle, in the order the prototype draws it (G17): an empty ring, a
 * sent plane, a clock that ran out, an arc still turning, a tick, a ban.
 *
 * The two in the middle — `expired` and `part` — are the ones a three-state
 * model misses: a link nobody opened before it died, and a person who signed in
 * and then wandered off half-registered. Both look fine on a status pill and
 * both mean the customer cannot book.
 */
export const ACCESS_STATES = ["none", "invited", "expired", "part", "live", "revoked"] as const;
export type AccessState = (typeof ACCESS_STATES)[number];

/**
 * The word beside the glyph.
 *
 * `invited` is **Link generated**, never *Link sent* (AC2). The app never
 * witnesses a send — the message leaves over WhatsApp, outside the system — so
 * "sent" would be a claim it cannot make, in the exact state Patricia checks
 * when a customer rings to say nothing arrived. It knows a link exists; it says
 * that and no more. The key stays `invited` because the prototype's own maps
 * use it; only the word is the contract.
 */
export const ACCESS_WORD: Record<AccessState, string> = {
  none: "Not registered",
  invited: "Link generated",
  expired: "Link expired",
  part: "Signing up",
  live: "Registered",
  revoked: "Access revoked",
};

/** What the desk should take from each state, in Patricia's terms (G17's sheet). */
export const ACCESS_MEANS: Record<AccessState, string> = {
  none: "Saved at the desk. No link has been made yet.",
  invited: "A link exists and has not been used. The only thing to watch is the expiry.",
  expired: "A day gone, never opened. Nothing wrong with the person.",
  part: "Signed in, then stopped before finishing. The identity is half-bound.",
  live: "Bound to their Google, Apple or Microsoft account, and signing in.",
  revoked: "Access taken away. The contact details stay.",
};

/**
 * How long a link is good for — **24 hours from ISSUE** (Freddi, 2026-09-09).
 *
 * Shorter than the week first recommended, and a real trade rather than a
 * detail: *Link expired* becomes the state Patricia sees most, so it has to read
 * as ordinary rather than as a fault.
 *
 * FROM ISSUE, not from send, and that was decided rather than defaulted. The
 * message leaves over WhatsApp, so the app cannot observe a send; the only
 * witnessable proxy was the *Copy message* tap, and building on it would have
 * bought a nullable sent-at, a "no clock yet" state, a re-copy restart and an
 * absolute cap — four pieces of machinery approximating a fact the system never
 * learns. Issue is a timestamp it genuinely owns.
 *
 * The prototype's `MEANS.expired`, `requirements.md` §3.4 and the portal
 * decision record all still say seven days. They are stale; this is the rule.
 */
export const INVITE_TTL_HOURS = 24;

const TTL_MS = INVITE_TTL_HOURS * 60 * 60 * 1000;

/** The expiry a link issued at `issuedAt` carries. */
export function inviteExpiresAt(issuedAt: Date): Date {
  return new Date(issuedAt.getTime() + TTL_MS);
}

/**
 * The four facts a `portal_invites` row holds, as the wire carries them.
 *
 * Timestamps are ISO strings rather than Dates because this shape travels
 * through JSON to the desk; every comparison below parses explicitly.
 */
export interface InviteFacts {
  token: string;
  issuedAt: string;
  expiresAt: string;
  /** Written on COMPLETED SIGN-IN, never on opening — see AC10. */
  usedAt: string | null;
  /** The verified email the completed sign-in bound, for AC11's "and by whom". */
  usedByEmail: string | null;
  /** Set by Revoke. The row is a tombstone, never deleted. */
  revokedAt: string | null;
}

/**
 * Which of the six a person is in, from their latest invite row and whether the
 * allowlist holds an entry for the email that used it.
 *
 * THE ORDER OF THESE CHECKS IS THE CONTRACT, and `revokedAt` leads it. Revoke
 * deletes the allowlist entry and LEAVES `usedAt` set — so any rule that asks
 * "was it used?" before "was it revoked?" reads a revoked person as *Signing
 * up*, which AC6a forbids by name. A PR review caught exactly that ordering
 * once; the test above is what stops it coming back.
 */
export function deriveAccessState(
  invite: InviteFacts | null,
  opts: {
    hasAccount: boolean;
    /**
     * Has the contact's `registered_at` been stamped? BINDING IS NOT
     * REGISTRATION (customer-sign-in-registration AC1): the allowlist entry
     * lands when the OAuth callback returns, before the person has confirmed
     * who they are or checked their dogs. *Registered* needs both the entry
     * and the stamp. Defaults to false — the fail-safe direction, since a
     * caller that forgets under-reports registration rather than inventing it.
     */
    registered?: boolean;
    now: Date;
  },
): AccessState {
  if (!invite) return "none";
  if (invite.revokedAt) return "revoked";
  if (!invite.usedAt) {
    // Exclusive at the instant: a link is live up to `expiresAt` and dead on it.
    return Date.parse(invite.expiresAt) > opts.now.getTime() ? "invited" : "expired";
  }
  // Used. The allowlist entry is the access; the invite row is only the audit
  // trail — so a bound token with no entry is somebody part-way through, and
  // so is a bound token whose person has not finished the registration steps.
  return opts.hasAccount && opts.registered === true ? "live" : "part";
}

/** The three things the desk can do. `new` issues, `reset` rebinds, `revoke` ends. */
export const ACCESS_ACTIONS_ALL = ["new", "reset", "revoke"] as const;
export type AccessAction = (typeof ACCESS_ACTIONS_ALL)[number];

/**
 * What each state offers beneath *Manage access*.
 *
 * Nothing bound means nothing to reset or revoke, so those four states offer
 * only **New link**. Where an identity IS bound — `part` as much as `live` —
 * the pair is **Reset registration** and **Revoke access**, and *New link* is
 * deliberately absent (AC4a): issuing a fresh link would leave the half-binding
 * standing behind it.
 */
export const ACCESS_ACTIONS: Record<AccessState, readonly AccessAction[]> = {
  none: ["new"],
  invited: ["new"],
  expired: ["new"],
  part: ["reset", "revoke"],
  live: ["reset", "revoke"],
  revoked: ["new"],
};

/**
 * Every one of the three CONFIRMS, and copying never does (AC8).
 *
 * New link kills an outstanding link, so it cuts off a customer part-way
 * through; Reset breaks a binding; Revoke ends access. Copy changes nothing —
 * and a confirmation on the routine action is what trains someone to click
 * through the destructive one.
 */
export const ACCESS_ACTION_LABEL: Record<AccessAction, string> = {
  new: "New link",
  reset: "Reset registration",
  revoke: "Revoke access",
};

/** The confirm wording, per action, with the person's first name in it. */
export function confirmMessage(action: AccessAction, firstName: string): string {
  if (action === "new") {
    return `Issue a new link for ${firstName}? The link above stops working straight away.`;
  }
  if (action === "reset") {
    return `Reset ${firstName}’s registration? The link above stops working and a new one takes its place.`;
  }
  return `Revoke ${firstName}’s access? They are signed out straight away and need a new link to get back in.`;
}

/** The word on the confirming button. */
export const CONFIRM_LABEL: Record<AccessAction, string> = {
  new: "New link",
  reset: "Reset",
  revoke: "Revoke",
};

/**
 * Is there a link worth copying?
 *
 * `invited` obviously, and `part` too — opening does not spend a link (AC10),
 * so somebody who signed in and got interrupted carries on with the very same
 * message.
 *
 * `live` ALSO, and that one is not about the registration link at all: a
 * registered customer has no token left, but Patricia still texts them "here is
 * where you book" (Freddi, 2026-09-12). The link in that message is the app's
 * own address, and the message says something different — see isRegistered.
 *
 * `expired` and `revoked` show the reason instead (AC6b); `none` has not made
 * one yet, which is what New link is for.
 */
export function isCopyable(state: AccessState): boolean {
  return state === "invited" || state === "part" || state === "live";
}

/**
 * Is this person's message about SIGNING UP, or about making a booking?
 *
 * The difference is whether they have an account, and it changes what the link
 * in the message even is (Freddi, 2026-09-12). Before: a registration link and
 * an explanation of what the app is for. After: the app itself, and a nudge to
 * use it — which is a text Patricia actually sends, and had nowhere to come
 * from while *Registered* was a row with nothing to copy.
 */
export function isRegistered(state: AccessState): boolean {
  return state === "live";
}

/**
 * Why a dead link is dead, in the place the draft would have been (AC6b).
 * `dateLabel` is the day as the row already renders it, so the reason and the
 * line under the name can never name two different days.
 */
export function deadLinkReason(state: AccessState, dateLabel: string): string | null {
  if (state === "expired") return `This link expired on ${dateLabel}. New link replaces it.`;
  if (state === "revoked") return `Access was revoked on ${dateLabel}. New link lets them back in.`;
  return null;
}

/** `9kLp3x` → `/join/9kLp3x`. The customer's door, spelled once. */
export function joinPath(token: string): string {
  return `/join/${token}`;
}


/**
 * The quiet second line under the name: a fact about the state, never a
 * restatement of it.
 *
 * `revoked` carries AC6a's sentence verbatim rather than the prototype's
 * "revoked 2 Sep · contact kept" — the criterion names the words, and the date
 * is not lost because `deadLinkReason` carries it two lines below.
 */
export function accessSubLine(state: AccessState, facts: { dateLabel?: string }): string {
  const on = facts.dateLabel ?? "";
  switch (state) {
    case "none":
      return "Link not sent yet";
    case "invited":
      return `Expires ${on}`;
    case "expired":
      // The state beside it says Link expired (2026-10-12), so the line says when and that it went unused.
      return `${on} · never used`;
    case "part":
      // The state beside it says Signing up (2026-10-12); "not finished" said it twice.
      return `Opened ${on}`;
    case "live":
      return `Signed in ${on}`;
    case "revoked":
      return "Access taken away. The contact details stay.";
  }
}

/**
 * Which of the row's own timestamps the line above should name.
 *
 * One place, because the row, the reason and the sub-line all have to agree on
 * which day they are talking about — and "expired Wed 3 Sep" beside "revoked 2
 * Sep" is the kind of disagreement nobody notices until a customer is on the
 * phone.
 */
export function subLineInstant(state: AccessState, invite: InviteFacts | null): string | null {
  if (!invite) return null;
  switch (state) {
    case "invited":
      return invite.expiresAt;
    case "expired":
      return invite.expiresAt;
    case "part":
    case "live":
      return invite.usedAt;
    case "revoked":
      return invite.revokedAt;
    default:
      return null;
  }
}

// `maskEmail` LIVED HERE AND IS GONE (Freddi, 2026-09-16: *"we should never be
// showing the email address registered with a link"*).
//
// It turned `cormac@example.com` into `c•••••@example.com` for the one screen
// that named whoever had spent a link. Masking is not anonymising: a first
// letter, a domain and a length identify somebody perfectly well to anyone who
// knows them, and the person holding a forwarded link is exactly who should
// not be learning that. With that screen's copy now generic, nothing in the
// app masks an address — and leaving the helper here would only be an
// invitation to show one again. The DESK still sees every address in full, on
// the App access column, where it is Patricia's to see.
